'use client'

import type { ReactNode } from 'react'

import { createContext, useContext, useMemo, useState } from 'react'
import { ChevronDownIcon, ChevronRightIcon } from 'lucide-react'

import {
	Collapsible,
	CollapsibleTrigger,
} from '@/shared/components/ui/collapsible'
import { cn } from '@/shared/lib/utils'

import { Checkbox } from './checkbox-native'

interface TreeNode {
	id: string
	name: string
	visible: boolean
	count?: number
	children?: TreeNode[]
}

type CheckedState = 'checked' | 'unchecked' | 'indeterminate'

interface TreeFilterContextValue {
	checked: Set<string>
	toggle: (ids: string[], value: boolean) => void
}

const TreeFilterContext = createContext<TreeFilterContextValue | null>(null)

function useTreeFilter(): TreeFilterContextValue {
	const ctx = useContext(TreeFilterContext)
	if (!ctx) throw new Error('Must be used within <TreeFilter>')
	return ctx
}

function getAllLeafIds(node: TreeNode): string[] {
	if (!node.children || node.children.length === 0) return [node.id]
	return node.children.flatMap(getAllLeafIds)
}

function getCheckedState(node: TreeNode, checked: Set<string>): CheckedState {
	const leaves = getAllLeafIds(node)
	if (leaves.length === 0) return checked.has(node.id) ? 'checked' : 'unchecked'
	const count = leaves.filter((id: string) => checked.has(id)).length
	if (count === 0) return 'unchecked'
	if (count === leaves.length) return 'checked'
	return 'indeterminate'
}

function filterTree(nodes: TreeNode[], query: string): TreeNode[] {
	if (!query) return nodes
	const result: TreeNode[] = []
	for (const node of nodes) {
		const match = node.name.toLowerCase().includes(query.toLowerCase())
		const filteredChildren = node.children
			? filterTree(node.children, query)
			: []

		result.push({
			...node,
			visible:
				match || filteredChildren.filter((child) => child.visible).length > 0,
			children: filteredChildren,
		})
	}
	return result
}

function TreeFilter({
	children,
	checked: _chacked,
	setChecked,
	className,
}: {
	children: ReactNode
	defaultChecked?: string[]
	checked: string[]
	setChecked: (value: string[]) => void
	className?: string
}) {
	const checked = useMemo(() => new Set(_chacked), [_chacked])
	const toggle = (ids: string[], value: boolean) => {
		const next = checked
		ids.forEach((id: string) => (value ? next.add(id) : next.delete(id)))
		setChecked(Array.from(next))
	}

	return (
		<TreeFilterContext.Provider value={{ checked: new Set(checked), toggle }}>
			<div
				className={cn(
					'flex h-full w-full flex-col overflow-hidden p-2 text-sm',
					className
				)}
			>
				{children}
			</div>
		</TreeFilterContext.Provider>
	)
}

function TreeFilterGroup({
	label,
	children,
}: {
	label?: string
	children: ReactNode
}) {
	return (
		<div className='flex-1 scroll-fade overflow-y-auto py-2'>
			{label && (
				<div className='px-3 pt-0.5 pb-2 text-xs font-semibold tracking-widest text-muted-foreground/50 uppercase'>
					{label}
				</div>
			)}
			{children}
		</div>
	)
}

function TreeFilterList({
	items,
	search,
	notFoundText,
}: {
	items: TreeNode[]
	search: string
	notFoundText: string
}) {
	const visible = filterTree(items, search)

	if (visible.length === 0) {
		return (
			<div className='px-3 py-4 text-center text-sm text-muted-foreground'>
				{notFoundText}
			</div>
		)
	}

	return (
		<>
			{visible
				.filter((node: TreeNode) => node.visible)
				.map((node: TreeNode) => (
					<TreeFilterNode key={node.id} node={node} depth={0} />
				))}
		</>
	)
}

function TreeFilterNode({
	node,
	depth = 0,
}: {
	node: TreeNode
	depth?: number
}) {
	const { checked, toggle } = useTreeFilter()
	const [expanded, setExpanded] = useState(false)

	const hasChildren = node.children && node.children.length > 0
	const checkedState = getCheckedState(node, checked)

	const handleCheck = () => {
		const leaves = getAllLeafIds(node)
		toggle(leaves.length ? leaves : [node.id], checkedState !== 'checked')
	}

	return (
		<Collapsible open={expanded} onOpenChange={setExpanded}>
			<div
				className='mx-1 flex cursor-pointer items-center gap-1.5 rounded-md py-1.25 pr-2.5 transition-colors duration-100 hover:bg-accent'
				style={{ paddingLeft: `${8 + depth * 10}px` }}
			>
				{hasChildren ? (
					<CollapsibleTrigger asChild>
						<button className='flex w-4 shrink-0 cursor-pointer items-center border-none bg-transparent p-0 text-muted-foreground'>
							<span className='flex items-center'>
								{expanded ? (
									<ChevronDownIcon className='size-4' />
								) : (
									<ChevronRightIcon className='size-4' />
								)}
							</span>
						</button>
					</CollapsibleTrigger>
				) : (
					<div className='w-4 shrink-0' />
				)}

				<Checkbox
					checked={checkedState === 'checked'}
					indeterminate={checkedState === 'indeterminate'}
					onChange={() => handleCheck()}
				/>

				<span
					onClick={handleCheck}
					className={`flex-1 cursor-pointer text-sm tracking-tight transition-colors duration-150 select-none ${
						checkedState === 'checked'
							? 'font-medium text-foreground'
							: 'font-normal text-muted-foreground'
					}`}
				>
					{node.name}
				</span>

				{node.count != null && (
					<span className='text-xs text-muted-foreground/50 tabular-nums'>
						{node.count}
					</span>
				)}
			</div>

			{expanded && hasChildren && (
				<div className='overflow-hidden'>
					{node
						.children!.filter((child: TreeNode) => child.visible)
						.map((child: TreeNode) => (
							<TreeFilterNode key={child.id} node={child} depth={depth + 1} />
						))}
				</div>
			)}
		</Collapsible>
	)
}

export { TreeFilter, TreeFilterGroup, TreeFilterList, TreeFilterNode }
